import {
  DEFAULT_AI_PHOTO_CONFIG_ID,
  normalizeAiPhotoConfigId,
  HEADER_MOCK_SCENARIO,
  HEADER_MODEL_OVERRIDE,
  HEADER_NONCE,
  HEADER_TURNSTILE,
  type ApiError,
  type CreatePredictionResponse,
  type NonceResponse,
  type PredictionResponse,
  type OpenAiBilling,
  type PublicAiPhotoCatalog,
} from '@ai-photo-style/shared';
import { EmbedError } from './errors';
import { parseCatalog } from './catalog';

const CATALOG_NOT_FOUND = Symbol('catalog endpoint not found');

/** Client voor ai-photo-style-api (cross-origin, zonder cookies). */
export class ApiClient {
  constructor(
    private readonly base: string,
    private readonly mockScenario?: string,
    private readonly modelOverride?: string,
    private readonly openaiBilling?: OpenAiBilling,
    private readonly catalogRevision?: string,
    private readonly configId?: string,
  ) {
    try { normalizeAiPhotoConfigId(configId); }
    catch { throw new EmbedError('CATALOG_INVALID'); }
  }

  private scopedPath(path: string): string {
    return this.configId === undefined ? path : `${path}?configId=${encodeURIComponent(this.configId)}`;
  }

  private validateResponseConfigId(response: unknown, code: 'CATALOG_INVALID' | 'NOT_FOUND' = 'CATALOG_INVALID'): void {
    try {
      // Only an omitted ID on a default response is compatible with legacy APIs.
      if (typeof response !== 'object' || response === null || Array.isArray(response) ||
        normalizeAiPhotoConfigId((response as { configId?: unknown }).configId) !== normalizeAiPhotoConfigId(this.configId)) {
        throw new EmbedError(code);
      }
    } catch { throw new EmbedError(code); }
  }

  /** null means an explicitly old API (HTTP 404), not a fetch or schema failure. */
  async getCatalog(signal?: AbortSignal): Promise<PublicAiPhotoCatalog | null> {
    const controller = new AbortController();
    const cancel = () => controller.abort();
    if (signal?.aborted) controller.abort();
    else signal?.addEventListener('abort', cancel, { once: true });
    let timedOut = false;
    const timer = globalThis.setTimeout(() => { timedOut = true; controller.abort(); }, 10_000);
    try {
      const body = await this.request<unknown>(this.scopedPath('/v1/catalog'), { signal: controller.signal }, this.configId === undefined);
      if (body === CATALOG_NOT_FOUND) return null;
      const catalog = parseCatalog(body);
      if (normalizeAiPhotoConfigId(catalog.configId) !== normalizeAiPhotoConfigId(this.configId)) throw new EmbedError('CATALOG_INVALID');
      return catalog;
    } catch (err) {
      if (timedOut) throw new EmbedError('CATALOG_UNAVAILABLE');
      throw err;
    } finally {
      globalThis.clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
    }
  }

  async createNonce(turnstileToken: string, sessionId: string, signal?: AbortSignal): Promise<NonceResponse> {
    const response = await this.request<NonceResponse>('/v1/nonce', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', [HEADER_TURNSTILE]: turnstileToken },
      body: JSON.stringify({ session_id: sessionId, ...(this.configId !== undefined ? { configId: this.configId } : {}) }),
      signal,
    });
    this.validateResponseConfigId(response);
    return response;
  }

  async createPrediction(
    nonce: string,
    sessionId: string,
    style: string,
    photo: Blob,
    coid: string,
    signal?: AbortSignal,
  ): Promise<CreatePredictionResponse> {
    const form = new FormData();
    form.append('style', style);
    form.append('session_id', sessionId);
    form.append('coid', coid);
    form.append('image', photo, 'foto.jpg');
    if (this.catalogRevision) form.append('catalog_revision', this.catalogRevision);
    if (this.configId !== undefined) form.append('configId', this.configId);
    // Preserve legacy HG billing during staged rollout. Managed APIs ignore this field and
    // choose the account only from their trusted Origin policy, even with a catalog revision.
    if (this.openaiBilling && normalizeAiPhotoConfigId(this.configId) === DEFAULT_AI_PHOTO_CONFIG_ID) form.append('openai_billing', this.openaiBilling);
    const headers: Record<string, string> = { [HEADER_NONCE]: nonce };
    if (this.mockScenario) headers[HEADER_MOCK_SCENARIO] = this.mockScenario;
    if (this.modelOverride) headers[HEADER_MODEL_OVERRIDE] = this.modelOverride;
    const response = await this.request<CreatePredictionResponse>('/v1/predictions', { method: 'POST', headers, body: form, signal });
    this.validateResponseConfigId(response);
    return response;
  }

  async getPrediction(id: string, signal?: AbortSignal): Promise<PredictionResponse> {
    const prediction = await this.request<PredictionResponse>(this.scopedPath(`/v1/predictions/${encodeURIComponent(id)}`), { signal });
    this.validateResponseConfigId(prediction, 'NOT_FOUND');
    return prediction;
  }

  async cancelPrediction(id: string): Promise<void> {
    try {
      await this.request(this.scopedPath(`/v1/predictions/${encodeURIComponent(id)}/cancel`), { method: 'POST' });
    } catch {
      /* annuleren is best effort */
    }
  }

  /** Haalt het resultaat op als blob; `resultUrl` mag relatief zijn t.o.v. de API. */
  async fetchResult(resultUrl: string, signal?: AbortSignal): Promise<Blob> {
    const url = new URL(resultUrl, `${this.base}/`);
    const ids = url.searchParams.getAll('configId');
    try {
      if (ids.length > 1 || (ids.length === 1 && normalizeAiPhotoConfigId(ids[0]) !== normalizeAiPhotoConfigId(this.configId))) {
        throw new EmbedError('NOT_FOUND');
      }
    } catch { throw new EmbedError('NOT_FOUND'); }
    if (this.configId !== undefined) url.searchParams.set('configId', this.configId);
    let res: Response;
    try {
      res = await fetch(url.toString(), { credentials: 'omit', signal });
    } catch (err) {
      throw toNetworkError(err);
    }
    if (!res.ok) throw new EmbedError(res.status === 404 ? 'NOT_FOUND' : 'INTERNAL');
    return res.blob();
  }

  private async request<T>(path: string, init: RequestInit, allowNotFound = false): Promise<T> {
    let res: Response;
    try {
      res = await fetch(`${this.base}${path}`, { ...init, credentials: 'omit', mode: 'cors' });
    } catch (err) {
      throw toNetworkError(err);
    }
    if (allowNotFound && res.status === 404) return CATALOG_NOT_FOUND as T;
    if (res.ok) {
      try {
        return (await res.json()) as T;
      } catch {
        throw new EmbedError(allowNotFound ? 'CATALOG_INVALID' : 'INTERNAL');
      }
    }
    let body: Partial<ApiError> = {};
    try {
      body = (await res.json()) as Partial<ApiError>;
    } catch {
      /* geen JSON */
    }
    throw new EmbedError(body.error ?? (res.status === 429 ? 'RATE_LIMITED' : 'INTERNAL'), body.retry_after_s);
  }
}

function toNetworkError(err: unknown): EmbedError {
  if (err instanceof DOMException && err.name === 'AbortError') return new EmbedError('CANCELED');
  return new EmbedError('NETWORK');
}
